import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { apiDelete, apiGet, apiPatch, apiPost, TENANT_TOKEN_KEY } from '../../api/client';
import { showGrowl } from '../../ui/growlState';
import { useAgentSelector } from '../../auth/AgentSelectorContext';

type RuleRow = {
  id: string;
  title: string | null;
  content: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /** none | compiling | hard | soft | error. `hard` = el sistema calcula y verifica la regla. */
  specStatus?: 'none' | 'compiling' | 'hard' | 'soft' | 'error';
  specSummary?: string | null;
  specPreview?: string[];
  specReason?: string | null;
};

const SPEC_CHIP: Record<NonNullable<RuleRow['specStatus']>, { label: string; color: string; bg: string }> = {
  hard: { label: 'Verificada', color: '#166534', bg: '#dcfce7' },
  compiling: { label: 'Interpretando…', color: '#92400e', bg: '#fef3c7' },
  soft: { label: 'Solo prompt', color: '#475569', bg: '#e2e8f0' },
  error: { label: 'Sin interpretar', color: '#991b1b', bg: '#fee2e2' },
  none: { label: 'Sin interpretar', color: '#475569', bg: '#e2e8f0' },
};

type AgentBlock = {
  id: string;
  name: string;
  model: string;
  isActive: boolean;
  ruleCount: number;
  activeRuleCount: number;
  rules: RuleRow[];
};

type RulesPayload = {
  maxActivePerAgent: number;
  agents: AgentBlock[];
};

const CONTENT_MAX = 4000;
const TITLE_MAX = 120;

function clip(s: string, n: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  if (t.length <= n) return t;
  return `${t.slice(0, n - 1)}…`;
}

function tableRow(rowIndex: number): CSSProperties {
  return {
    borderTop: '1px solid #e2e8f0',
    background: rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc',
  };
}

function IconPlus({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconTrash({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

export function RulesPage() {
  const token = localStorage.getItem(TENANT_TOKEN_KEY)!;
  const { selectedAgentId } = useAgentSelector();
  const [data, setData] = useState<RulesPayload | null>(null);
  const [err, setErr] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [search, setSearch] = useState('');
  const [helpOpen, setHelpOpen] = useState(true);
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({});

  const [addAgentId, setAddAgentId] = useState<string | null>(null);
  const [nTitle, setNTitle] = useState('');
  const [nContent, setNContent] = useState('');
  const [nBusy, setNBusy] = useState(false);

  const [editing, setEditing] = useState<{ agentId: string; rule: RuleRow } | null>(null);
  const [eTitle, setETitle] = useState('');
  const [eContent, setEContent] = useState('');
  const [eActive, setEActive] = useState(true);
  const [eBusy, setEBusy] = useState(false);

  const [deleteModal, setDeleteModal] = useState<{ agentId: string; rule: RuleRow } | null>(null);
  const [deleteAllModal, setDeleteAllModal] = useState<AgentBlock | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const d = await apiGet<RulesPayload>('/api/console/rules', token);
      setData(d);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  // Mientras alguna regla se interpreta, se refresca hasta que termine.
  const anyCompiling = useMemo(
    () => data?.agents.some((a) => a.rules.some((r) => r.specStatus === 'compiling')) ?? false,
    [data],
  );
  useEffect(() => {
    if (!anyCompiling) return;
    const timer = window.setInterval(() => void load(), 3000);
    return () => window.clearInterval(timer);
  }, [anyCompiling, load]);

  useEffect(() => {
    if (!editing && !deleteModal && !deleteAllModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !eBusy && !deleteBusy) {
        setEditing(null);
        setDeleteModal(null);
        setDeleteAllModal(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editing, deleteModal, deleteAllModal, eBusy, deleteBusy]);

  const statsByModel = useMemo(() => {
    if (!data) return {} as Record<string, { agents: number; total: number; active: number }>;
    const out: Record<string, { agents: number; total: number; active: number }> = {};
    for (const a of data.agents) {
      const m = a.model?.trim() || '—';
      if (!out[m]) out[m] = { agents: 0, total: 0, active: 0 };
      out[m]!.agents += 1;
      out[m]!.total += a.ruleCount;
      out[m]!.active += a.activeRuleCount;
    }
    return out;
  }, [data]);

  const modelOptions = useMemo(() => Object.keys(statsByModel).sort((a, b) => a.localeCompare(b)), [statsByModel]);

  const visibleAgents = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.agents.filter((a) => {
      if (selectedAgentId && a.id !== selectedAgentId) return false;
      if (modelFilter && (a.model?.trim() || '—') !== modelFilter) return false;
      if (!q) return true;
      if (a.name.toLowerCase().includes(q)) return true;
      return a.rules.some(
        (r) =>
          r.content.toLowerCase().includes(q) || (r.title?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [data, modelFilter, search, selectedAgentId]);

  const maxActive = data?.maxActivePerAgent ?? 40;

  const toggleSection = (agentId: string) => {
    setSectionOpen((s) => ({ ...s, [agentId]: !(s[agentId] ?? true) }));
  };

  const startAdd = (agentId: string) => {
    setSectionOpen((s) => ({ ...s, [agentId]: true }));
    setAddAgentId((x) => (x === agentId ? null : agentId));
    setNTitle('');
    setNContent('');
  };

  const submitAdd = async (e: FormEvent, agentId: string) => {
    e.preventDefault();
    const content = nContent.trim();
    if (!content) {
      setErr('Escribí el contenido de la regla.');
      return;
    }
    if (content.length > CONTENT_MAX) {
      setErr(`La regla no puede superar ${CONTENT_MAX} caracteres.`);
      return;
    }
    setNBusy(true);
    setErr('');
    try {
      await apiPost(
        `/api/console/agents/${agentId}/rules`,
        { title: nTitle.trim() || undefined, content },
        token,
        { successMessage: 'Regla agregada' }
      );
      setAddAgentId(null);
      setNTitle('');
      setNContent('');
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setNBusy(false);
    }
  };

  const openEdit = (agentId: string, rule: RuleRow) => {
    setSectionOpen((s) => ({ ...s, [agentId]: true }));
    setEditing({ agentId, rule });
    setETitle(rule.title ?? '');
    setEContent(rule.content);
    setEActive(rule.isActive);
  };

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const content = eContent.trim();
    if (!content) {
      setErr('El contenido no puede estar vacío.');
      return;
    }
    setEBusy(true);
    setErr('');
    try {
      await apiPatch(
        `/api/console/agents/${editing.agentId}/rules/${editing.rule.id}`,
        { title: eTitle.trim() || null, content, isActive: eActive },
        token,
        { successMessage: 'Regla actualizada' }
      );
      setEditing(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setEBusy(false);
    }
  };

  const toggleActive = async (agentId: string, rule: RuleRow) => {
    setErr('');
    try {
      await apiPatch(
        `/api/console/agents/${agentId}/rules/${rule.id}`,
        { isActive: !rule.isActive },
        token,
        { successMessage: rule.isActive ? 'Regla desactivada' : 'Regla activada' }
      );
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const moveRule = async (agent: AgentBlock, rule: RuleRow, dir: -1 | 1) => {
    const idx = agent.rules.findIndex((r) => r.id === rule.id);
    const swap = agent.rules[idx + dir];
    if (!swap) return;
    setErr('');
    try {
      await apiPatch(`/api/console/agents/${agent.id}/rules/${rule.id}`, { sortOrder: swap.sortOrder }, token, {
        showErrorGrowl: true,
      });
      await apiPatch(`/api/console/agents/${agent.id}/rules/${swap.id}`, { sortOrder: rule.sortOrder }, token, {
        showErrorGrowl: true,
      });
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    }
  };

  const confirmDeleteRule = async () => {
    if (!deleteModal) return;
    setDeleteBusy(true);
    setErr('');
    try {
      await apiDelete(`/api/console/agents/${deleteModal.agentId}/rules/${deleteModal.rule.id}`, token, {
        successMessage: 'Regla eliminada',
      });
      setDeleteModal(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmDeleteAll = async () => {
    if (!deleteAllModal || deleteAllModal.ruleCount === 0) return;
    setDeleteBusy(true);
    setErr('');
    try {
      const out = await apiDelete<{ deleted: number }>(
        `/api/console/agents/${deleteAllModal.id}/rules`,
        token,
        { successMessage: 'Reglas eliminadas' }
      );
      showGrowl(`Se eliminaron ${out.deleted} regla${out.deleted === 1 ? '' : 's'}.`, 'success');
      setDeleteAllModal(null);
      void load();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <>
      <div style={pageShell}>
        <div style={pageScroll}>
          <div style={pageHeaderRow}>
            <div>
              <h1 style={pageTitle}>Reglas de respuesta</h1>
              <p style={pageSubtitle}>
                Patrones que disparan respuestas automáticas antes de consultar al modelo.
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <button type="button" onClick={() => void load()} style={btnGhost} title="Recargar">
                ⟳ Actualizar
              </button>
              <button type="button" onClick={() => setHelpOpen((o) => !o)} style={btnGhost} aria-expanded={helpOpen}>
                {helpOpen ? 'Ocultar ayuda' : 'Cómo funciona'}
              </button>
            </div>
          </div>

          {helpOpen ? (
            <div style={helpBox}>
              <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.65, maxWidth: 920 }}>
                <strong>Reglas vs aprendizajes:</strong> las reglas son prescripciones globales («nunca…», «siempre…»,
                tono, políticas de reserva). Los{' '}
                <Link to="/app/learnings" style={{ color: '#7c3aed', fontWeight: 600 }}>
                  aprendizajes
                </Link>{' '}
                son ejemplos por tema (visitante → respuesta deseada). Si hay conflicto, <strong>prevalece la
                regla</strong>. Máximo {maxActive} reglas activas por agente; el orden define cómo aparecen en el
                prompt.
              </p>
            </div>
          ) : null}

          {data ? (
            <div style={filterBar}>
              <label style={filterLabel}>
                <span>Modelo LLM</span>
                <select value={modelFilter} onChange={(e) => setModelFilter(e.target.value)} style={selectFilter}>
                  <option value="">Todos los modelos</option>
                  {modelOptions.map((m) => (
                    <option key={m} value={m}>
                      {m} — {statsByModel[m]!.active} activas / {statsByModel[m]!.total} total ·{' '}
                      {statsByModel[m]!.agents} agente(s)
                    </option>
                  ))}
                </select>
              </label>
              <label style={filterLabel}>
                <span>Buscar</span>
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Agente o texto de regla…"
                  style={searchInput}
                />
              </label>
            </div>
          ) : null}

          {err ? (
            <div role="alert" style={errBox}>
              {err}
            </div>
          ) : null}

          {!data && !err ? (
            <div style={emptyState}>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#475569' }}>Cargando reglas…</p>
            </div>
          ) : !data ? null : data.agents.length === 0 ? (
            <div style={emptyState}>
              <p style={{ margin: 0, fontSize: 15, color: '#475569' }}>
                No hay agentes. Creá uno en{' '}
                <Link to="/app/agents" style={{ color: '#7c3aed', fontWeight: 600 }}>
                  Agentes virtuales
                </Link>
                .
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {visibleAgents.length === 0 ? (
                <p style={{ color: '#64748b', fontSize: 14 }}>Ningún agente coincide con el filtro.</p>
              ) : null}
              {visibleAgents.map((agent) => {
                const open = sectionOpen[agent.id] ?? true;
                const atLimit = agent.activeRuleCount >= maxActive;
                return (
                  <section key={agent.id} style={agentCard}>
                    <div style={agentCardHeader}>
                      <button
                        type="button"
                        onClick={() => toggleSection(agent.id)}
                        style={collapseBtn}
                        aria-expanded={open}
                      >
                        <span style={{ transform: open ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▸</span>
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                          <strong style={{ fontSize: 16, color: '#0f172a' }}>{agent.name}</strong>
                          {!agent.isActive ? (
                            <span style={badgeMuted}>Pausado</span>
                          ) : null}
                          <span style={badgeCount}>
                            {agent.activeRuleCount} activas / {agent.ruleCount} total
                          </span>
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>{agent.model}</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => startAdd(agent.id)}
                          style={btnPrimarySoft}
                          disabled={atLimit && addAgentId !== agent.id}
                          title={atLimit ? `Máximo ${maxActive} reglas activas` : 'Nueva regla'}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <IconPlus size={16} />
                            Regla
                          </span>
                        </button>
                        {agent.ruleCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => setDeleteAllModal(agent)}
                            style={btnDangerOutline}
                            title="Eliminar todas las reglas de este agente"
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <IconTrash size={16} />
                              Todas
                            </span>
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {open ? (
                      <div style={{ padding: '0 20px 20px' }}>
                        {addAgentId === agent.id ? (
                          <form
                            onSubmit={(e) => void submitAdd(e, agent.id)}
                            style={{
                              marginTop: 16,
                              padding: 16,
                              borderRadius: 12,
                              background: '#fffbeb',
                              border: '1px solid #fde68a',
                            }}
                          >
                            <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 700, color: '#92400e' }}>
                              Nueva regla
                            </p>
                            <label style={fieldLabel}>
                              Título (opcional)
                              <input
                                value={nTitle}
                                onChange={(e) => setNTitle(e.target.value.slice(0, TITLE_MAX))}
                                placeholder="Ej. Reservas, Tono, Precios"
                                style={inputField}
                                maxLength={TITLE_MAX}
                              />
                            </label>
                            <label style={fieldLabel}>
                              Regla *
                              <textarea
                                value={nContent}
                                onChange={(e) => setNContent(e.target.value.slice(0, CONTENT_MAX))}
                                rows={4}
                                placeholder="Ej. Nunca confirmes una reserva sin nombre y un contacto (email o teléfono)."
                                style={textareaField}
                                required
                              />
                              <span style={charHint}>
                                {nContent.length}/{CONTENT_MAX}
                              </span>
                            </label>
                            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                              <button type="submit" disabled={nBusy} style={btnPri}>
                                {nBusy ? 'Guardando…' : 'Guardar regla'}
                              </button>
                              <button type="button" onClick={() => setAddAgentId(null)} style={btnOutline}>
                                Cancelar
                              </button>
                            </div>
                          </form>
                        ) : null}

                        {agent.rules.length === 0 ? (
                          <p style={{ margin: '16px 0 0', color: '#64748b', fontSize: 14 }}>
                            Sin reglas. Agregá instrucciones que el bot debe cumplir siempre.
                          </p>
                        ) : (
                          <div style={{ marginTop: 16, overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                              <thead>
                                <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                                  <th style={thCell}>Orden</th>
                                  <th style={thCell}>Estado</th>
                                  <th style={thCell}>Regla</th>
                                  <th style={{ ...thCell, width: 160 }}>Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {agent.rules.map((rule, i) => (
                                  <tr key={rule.id} style={tableRow(i)}>
                                    <td style={tdCell}>
                                      <div style={{ display: 'flex', gap: 4 }}>
                                        <button
                                          type="button"
                                          style={btnSm}
                                          disabled={i === 0}
                                          onClick={() => void moveRule(agent, rule, -1)}
                                          title="Subir"
                                        >
                                          ↑
                                        </button>
                                        <button
                                          type="button"
                                          style={btnSm}
                                          disabled={i === agent.rules.length - 1}
                                          onClick={() => void moveRule(agent, rule, 1)}
                                          title="Bajar"
                                        >
                                          ↓
                                        </button>
                                      </div>
                                    </td>
                                    <td style={tdCell}>
                                      <button
                                        type="button"
                                        onClick={() => void toggleActive(agent.id, rule)}
                                        style={rule.isActive ? badgeActive : badgeInactive}
                                      >
                                        {rule.isActive ? 'Activa' : 'Inactiva'}
                                      </button>
                                    </td>
                                    <td style={tdCell}>
                                      {rule.title ? (
                                        <strong style={{ display: 'block', marginBottom: 4, color: '#0f172a' }}>
                                          {rule.title}
                                        </strong>
                                      ) : null}
                                      <span style={{ color: '#334155', lineHeight: 1.5 }} title={rule.content}>
                                        {clip(rule.content, 280)}
                                      </span>
                                      {(() => {
                                        const chip = SPEC_CHIP[rule.specStatus ?? 'none'];
                                        return (
                                          <div style={{ marginTop: 6 }}>
                                            <span
                                              style={{
                                                display: 'inline-block',
                                                fontSize: 11,
                                                fontWeight: 600,
                                                padding: '2px 8px',
                                                borderRadius: 999,
                                                color: chip.color,
                                                background: chip.bg,
                                              }}
                                              title={
                                                rule.specStatus === 'hard'
                                                  ? 'El sistema calcula y revisa esta regla en cada respuesta.'
                                                  : 'Va en el prompt y se audita después, pero no se verifica con cálculo.'
                                              }
                                            >
                                              {chip.label}
                                            </span>
                                            {rule.specStatus === 'hard' && rule.specSummary ? (
                                              <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
                                                {rule.specSummary}
                                                {rule.specPreview?.map((line) => (
                                                  <div key={line} style={{ color: '#64748b' }}>
                                                    · {line}
                                                  </div>
                                                ))}
                                              </div>
                                            ) : null}
                                            {(rule.specStatus === 'soft' || rule.specStatus === 'error') && rule.specReason ? (
                                              <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                                                {rule.specReason}
                                              </div>
                                            ) : null}
                                          </div>
                                        );
                                      })()}
                                    </td>
                                    <td style={tdCell}>
                                      <button type="button" style={btnSm} onClick={() => openEdit(agent.id, rule)}>
                                        Editar
                                      </button>
                                      <button
                                        type="button"
                                        style={{ ...btnSm, color: '#b91c1c', borderColor: '#fecaca' }}
                                        onClick={() => setDeleteModal({ agentId: agent.id, rule })}
                                      >
                                        Borrar
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editing
        ? createPortal(
            <div
              role="dialog"
              aria-modal
              aria-labelledby="rules-edit-title"
              style={modalOverlay}
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget && !eBusy) setEditing(null);
              }}
            >
              <form onSubmit={(e) => void saveEdit(e)} style={modalPanel} onMouseDown={(e) => e.stopPropagation()}>
                <h2 id="rules-edit-title" style={modalTitle}>
                  Editar regla
                </h2>
                <label style={fieldLabel}>
                  Título (opcional)
                  <input
                    value={eTitle}
                    onChange={(e) => setETitle(e.target.value.slice(0, TITLE_MAX))}
                    style={inputField}
                    maxLength={TITLE_MAX}
                  />
                </label>
                <label style={fieldLabel}>
                  Regla
                  <textarea
                    value={eContent}
                    onChange={(e) => setEContent(e.target.value.slice(0, CONTENT_MAX))}
                    rows={7}
                    style={textareaField}
                    required
                  />
                  <span style={charHint}>
                    {eContent.length}/{CONTENT_MAX}
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 16 }}>
                  <input type="checkbox" checked={eActive} onChange={(e) => setEActive(e.target.checked)} />
                  Activa en el prompt
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" disabled={eBusy} style={btnPri}>
                    {eBusy ? 'Guardando…' : 'Guardar'}
                  </button>
                  <button type="button" disabled={eBusy} onClick={() => setEditing(null)} style={btnOutline}>
                    Cancelar
                  </button>
                </div>
              </form>
            </div>,
            document.body
          )
        : null}

      {deleteModal
        ? createPortal(
            <div
              role="dialog"
              aria-modal
              style={modalOverlay}
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget && !deleteBusy) setDeleteModal(null);
              }}
            >
              <div style={modalPanel} onMouseDown={(e) => e.stopPropagation()}>
                <h2 style={modalTitle}>Eliminar regla</h2>
                <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.5 }}>
                  ¿Eliminar {deleteModal.rule.title ? `«${deleteModal.rule.title}»` : 'esta regla'}? No se puede deshacer.
                </p>
                <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                  <button type="button" disabled={deleteBusy} onClick={() => void confirmDeleteRule()} style={btnModalDanger}>
                    {deleteBusy ? 'Eliminando…' : 'Eliminar'}
                  </button>
                  <button type="button" disabled={deleteBusy} onClick={() => setDeleteModal(null)} style={btnOutline}>
                    Cancelar
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {deleteAllModal
        ? createPortal(
            <div
              role="dialog"
              aria-modal
              style={modalOverlay}
              onMouseDown={(ev) => {
                if (ev.target === ev.currentTarget && !deleteBusy) setDeleteAllModal(null);
              }}
            >
              <div style={modalPanel} onMouseDown={(e) => e.stopPropagation()}>
                <h2 style={modalTitle}>Eliminar todas las reglas</h2>
                <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.5 }}>
                  Se van a eliminar <strong>{deleteAllModal.ruleCount}</strong> regla
                  {deleteAllModal.ruleCount === 1 ? '' : 's'} de <strong>{deleteAllModal.name}</strong>.
                </p>
                <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                  <button type="button" disabled={deleteBusy} onClick={() => void confirmDeleteAll()} style={btnModalDanger}>
                    {deleteBusy ? 'Eliminando…' : 'Eliminar todas'}
                  </button>
                  <button type="button" disabled={deleteBusy} onClick={() => setDeleteAllModal(null)} style={btnOutline}>
                    Cancelar
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

const pageShell: CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: 1080,
  margin: '0 auto',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const pageScroll: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '28px 24px 40px',
  WebkitOverflowScrolling: 'touch',
};

const pageHeaderRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 20,
};

const pageTitle: CSSProperties = {
  margin: 0,
  fontSize: 22,
  fontWeight: 800,
  letterSpacing: '-0.02em',
  color: '#0f172a',
};

const pageSubtitle: CSSProperties = {
  margin: '6px 0 0',
  fontSize: 14,
  color: '#64748b',
};

const helpBox: CSSProperties = {
  marginBottom: 22,
  padding: '16px 18px',
  borderRadius: 12,
  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  border: '1px solid #e2e8f0',
  borderLeft: '4px solid #0ea5e9',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
};

const filterBar: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'center',
  marginBottom: 24,
  padding: '12px 16px',
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
};

const filterLabel: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 10,
  fontSize: 13,
  fontWeight: 600,
  color: '#334155',
};

const selectFilter: CSSProperties = {
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  fontSize: 14,
  minWidth: 220,
  maxWidth: '100%',
  background: '#fff',
};

const searchInput: CSSProperties = {
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  fontSize: 14,
  minWidth: 200,
  maxWidth: '100%',
};

const errBox: CSSProperties = {
  marginBottom: 16,
  padding: '12px 14px',
  borderRadius: 10,
  background: '#fef2f2',
  border: '1px solid #fecaca',
  color: '#991b1b',
  fontSize: 14,
};

const emptyState: CSSProperties = {
  padding: '40px 24px',
  textAlign: 'center',
  borderRadius: 16,
  background: '#f8fafc',
  border: '1px dashed #e2e8f0',
};

const agentCard: CSSProperties = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06), 0 4px 12px rgba(15, 23, 42, 0.04)',
};

const agentCardHeader: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 12,
  padding: '18px 20px',
  background: 'linear-gradient(180deg, #fafafa 0%, #fff 100%)',
  borderBottom: '1px solid #f1f5f9',
};

const collapseBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 14,
  color: '#64748b',
  padding: '4px 6px',
  lineHeight: 1,
};

const badgeCount: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 999,
  background: '#e0f2fe',
  color: '#0369a1',
};

const badgeMuted: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 999,
  background: '#f1f5f9',
  color: '#64748b',
};

const badgeActive: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  padding: '4px 10px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  background: '#dcfce7',
  color: '#166534',
};

const badgeInactive: CSSProperties = {
  ...badgeActive,
  background: '#f1f5f9',
  color: '#64748b',
};

const thCell: CSSProperties = { padding: '0.65rem 1rem', fontSize: 12, fontWeight: 700, color: '#475569' };
const tdCell: CSSProperties = { padding: '0.65rem 1rem', verticalAlign: 'top' };

const fieldLabel: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#334155',
  marginBottom: 12,
};

const inputField: CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 6,
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  fontSize: 14,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const textareaField: CSSProperties = {
  ...inputField,
  resize: 'vertical',
  minHeight: 88,
};

const charHint: CSSProperties = {
  display: 'block',
  marginTop: 4,
  fontSize: 11,
  color: '#94a3b8',
  fontWeight: 500,
};

const btnGhost: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#475569',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
};

const btnPri: CSSProperties = {
  padding: '10px 18px',
  borderRadius: 10,
  border: 'none',
  background: '#0ea5e9',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};

const btnOutline: CSSProperties = {
  padding: '9px 14px',
  borderRadius: 10,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#334155',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13,
};

const btnPrimarySoft: CSSProperties = {
  ...btnOutline,
  background: '#e0f2fe',
  borderColor: '#7dd3fc',
  color: '#0369a1',
};

const btnDangerOutline: CSSProperties = {
  ...btnOutline,
  borderColor: '#fecaca',
  color: '#b91c1c',
  background: '#fff',
};

const btnSm: CSSProperties = {
  padding: '6px 10px',
  marginRight: 6,
  fontSize: 12,
  fontWeight: 600,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  color: '#334155',
  cursor: 'pointer',
};

const modalOverlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  backdropFilter: 'blur(3px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
  zIndex: 100,
};

const modalPanel: CSSProperties = {
  background: '#fff',
  borderRadius: 16,
  padding: 24,
  maxWidth: 520,
  width: '100%',
  maxHeight: 'min(90vh, 720px)',
  overflow: 'auto',
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.28)',
  border: '1px solid #e2e8f0',
};

const modalTitle: CSSProperties = {
  marginTop: 0,
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: '1px solid #f1f5f9',
  fontSize: 18,
  fontWeight: 700,
  color: '#0f172a',
};

const btnModalDanger: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 10,
  border: '1px solid #b91c1c',
  background: '#b91c1c',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};
